import { RestrictionsCollection } from "../restrictions-collection";
import {IRestrictionsStrategy} from "./restrictions.strategy.interface";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IRawRestrictionType} from "../../firebase/topics/messages/raw-data/restrictions-update.raw-data";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {LocalStorageKeys} from "../../storage/local-storage-keys";
import {Check} from "@sparkware/app-utils";
import {computeRestrictionsExpirationDate} from "./compute-restrictions-expiration-date";

export class AuthorizedUserRestrictionsStrategy implements IRestrictionsStrategy {
    constructor(private readonly services: IServiceFactoryExtended) {

        this._restrictions = this._createRestrictionsCollection([]);

        this.services.firebase.topics.messages.events.restrictionsUpdate.registerListener(this._onRestrictionsUpdateHandler);

        reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if(isAuthorized) {
                    this.restrictions = this._readRestrictions();
                } else {
                    this._clearRestrictions();
                }
            }, {
                fireImmediately: true
            })

        makeObservable(this);

    }

    @observable.ref
    private _restrictions: RestrictionsCollection;
    get restrictions(): RestrictionsCollection {
        return this._restrictions;
    }

    private set restrictions(value: RestrictionsCollection) {
        runInAction(() => {
            this._restrictions = value;
        });
    }

    private _createRestrictionsCollection(rawRestrictions: IRawRestrictionType | IRawRestrictionType[]) {
        if(!rawRestrictions) {
            rawRestrictions = [];
        } else if(!Check.isArray(rawRestrictions)) {
            rawRestrictions = [rawRestrictions];
        }

        if(rawRestrictions.length === 0) {
            this.services.localStorage.removeItem(LocalStorageKeys.authorizedUserRestrictions);
        } else {
            computeRestrictionsExpirationDate(rawRestrictions);
            this.services.localStorage.setJson(LocalStorageKeys.authorizedUserRestrictions, rawRestrictions);
        }



        return new RestrictionsCollection(rawRestrictions, this.services)
    }


    private _onRestrictionsUpdateHandler = (rawRestrictions: IRawRestrictionType[]) => {
        this.restrictions = this._createRestrictionsCollection(rawRestrictions);

    }

    private _clearRestrictions(): void {
       this.restrictions = this._createRestrictionsCollection([]);
    }


    private _readRestrictions(): RestrictionsCollection {
        if(!this.services.user.isAuthorized) {
            return this._createRestrictionsCollection([]);
        }

        let restrictions = this.services.localStorage.getJson<IRawRestrictionType[]>(LocalStorageKeys.authorizedUserRestrictions) ?? [];
        if(restrictions.length > 0) {
            return this._createRestrictionsCollection(restrictions);
        }

        const tokenRestrictions = this.services.sdk.extractTokenProperty("restrictions");
        if(!tokenRestrictions) {
            return this._createRestrictionsCollection([]);
        }

        try {
            return this._createRestrictionsCollection(JSON.parse(tokenRestrictions));
        } catch (err) {
            this.services.logger.error('Failed to parse restrictions from token', err);
        }


    }
}